import "./RemainingPagesResponsive.css";
import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import api, { assetUrl } from "../api";
import UserLayout from "../components/Userlayout";
import { money, fmtDate, fmtDateTime, customerVisibleVersions, customerQuoteState, numberVersions } from "../quoteUtils";
import QuoteCompare from "../components/QuoteCompare";

const VERSION_STATUS = {
  sent: { label: "Current", cls: "bg-blue-50 text-blue-700" },
  approved: { label: "Approved", cls: "bg-green-50 text-green-700" },
  declined: { label: "Declined", cls: "bg-red-50 text-red-600" },
  overridden: { label: "Replaced", cls: "bg-slate-100 text-slate-500" },
};

export default function QuoteHistory() {
  const { id } = useParams();
  const [request, setRequest] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openCompare, setOpenCompare] = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const [r, q] = await Promise.all([api.get(`/requests/${id}`), api.get(`/requests/${id}/quotations`)]);
      setRequest(r.data);
      const list = numberVersions(customerVisibleVersions(r.data, Array.isArray(q.data) ? q.data : []), { useStored: false });
      setQuotes(list);
      if (list.length > 1) setOpenCompare(list[0]._id); // show latest changes straight away
    } catch (e) {
      setError(e.response?.data?.message || "Could not load quote history.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const state = request ? customerQuoteState(request) : null;
  const current = quotes[0];

  return (
    <UserLayout>
      <main className="mx-auto w-full max-w-5xl space-y-5 px-4 py-8 md:px-8">
        <Link to={`/quotes/${id}`} className="text-sm font-semibold text-red-700">← Back to quote</Link>
        <div>
          <h1 className="text-3xl font-bold">All quotes</h1>
          <p className="text-slate-500">
            Every quote we've sent for {request?.requestId || "this request"}, newest first. Earlier quotes are kept so you can see what changed.
            {state === "sent" && " Approve or decline the current version from the quote page."}
          </p>
        </div>

        {loading && <p>Loading quotations…</p>}
        {error && <p role="alert" className="rounded-xl bg-red-50 p-4 text-red-700">{error}</p>}
        {!loading && !error && !quotes.length && <p className="text-slate-500">No quotes have been sent for this request yet.</p>}

        {quotes.map((q, idx) => {
          const isCurrent = q._id === current?._id;
          const meta = isCurrent && state === "updating"
            ? { label: "Being updated", cls: "bg-slate-100 text-slate-600" }
            : isCurrent && state === "revision_requested"
              ? { label: "Revision requested", cls: "bg-purple-50 text-purple-700" }
              : VERSION_STATUS[q.status] || VERSION_STATUS.overridden;
          return (
            <article key={q._id} className={`space-y-4 rounded-2xl border bg-white p-5 shadow-sm ${isCurrent ? "border-slate-300" : "border-slate-200 opacity-90"}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold">
                    Quote {q._v} <span className="text-sm font-normal text-slate-400">{isCurrent ? "latest" : "earlier"} · {q.quoteId}</span>
                  </h2>
                  <p className="text-sm text-slate-500">{fmtDateTime(q.createdAt)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold">{money(q.pricing?.total)}</p>
                  <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${meta.cls}`}>{meta.label}</span>
                </div>
              </div>

              <div className="divide-y divide-slate-100">
                {(q.parts || []).map((part, i) => (
                  <div key={i} className="flex items-center gap-4 py-3">
                    {part.image && <img alt="" src={assetUrl(part.image)} className="h-14 w-14 rounded-lg object-cover" />}
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold">{part.name || part.partNumber || "Part"}</p>
                      <p className="text-xs text-slate-500">{[part.vehicle, part.condition, `Qty ${part.quantity || 1}`].filter(Boolean).join(" · ")}</p>
                    </div>
                    <p className="font-semibold">{money((part.quantity || 1) * (part.unitPrice || 0))}</p>
                  </div>
                ))}
              </div>

              <div className="grid gap-1 text-sm text-slate-600 sm:grid-cols-4">
                <p>Parts: {money(q.pricing?.subtotal)}</p>
                <p>Freight: {money(q.pricing?.freightTotal)}</p>
                <p>Core: {money(q.pricing?.coreChargeTotal)}</p>
              </div>
              <div className="grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
                <p>Warranty: {q.warranty || "Not specified"}</p>
                <p>Delivery: {q.deliveryType || "Not specified"}{q.estDelivery ? ` (est. ${fmtDate(q.estDelivery)})` : ""}</p>
              </div>
              {q.customerNote && <p className="text-sm text-slate-600 whitespace-pre-line">{q.customerNote}</p>}
              {(q.attachments || []).length > 0 && (
                <div className="flex flex-wrap gap-3">
                  {q.attachments.map((a, i) => (
                    <a key={i} className="text-sm font-semibold text-red-700 underline" href={assetUrl(a.url)} target="_blank" rel="noreferrer">
                      {a.name || `Attachment ${i + 1}`}
                    </a>
                  ))}
                </div>
              )}
              {quotes[idx + 1] && (
                <div>
                  <button onClick={() => setOpenCompare(openCompare === q._id ? null : q._id)}
                    className="text-sm font-semibold text-blue-700 hover:underline">
                    {openCompare === q._id ? "Hide changes" : `What changed from Quote ${quotes[idx + 1]._v}`}
                  </button>
                  {openCompare === q._id && (
                    <div className="mt-3">
                      <QuoteCompare before={quotes[idx + 1]} after={q} beforeLabel={`Quote ${quotes[idx + 1]._v}`} afterLabel={`Quote ${q._v}`} />
                    </div>
                  )}
                </div>
              )}
              {isCurrent && state === "sent" && (
                <Link to={`/quotes/${id}`} className="inline-flex rounded-xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white">
                  Respond to this quote
                </Link>
              )}
            </article>
          );
        })}
      </main>
    </UserLayout>
  );
}
